import React, { useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import TableC from "@/CommonElements/TableC";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useActivityLogsQuery } from "@/services/activityLogs";
import { Link } from "react-router";
import { DATE_TIME_FORMAT, DATE_TIME_FULL_FORMAT } from "@/utils/constants";
import i18n from "@/i18n";
import { formatDate } from "@/utils/helpers";

const PAGE_SIZE = 15;
const EMPTY_VALUE = "-";

const DEFAULT_VISIBLE_COLUMN_KEYS = ["id", "log_name", "event", "description", "actor", "ip_address", "created_at"];

 

const getTranslatedLabel = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const createDateFilterField = (name, label) => ({
  name,
  label,
  colSize: { md: 6 },
  render: ({ controllerField, placeholder }) => (
    <DatePicker
      size="large"
      className="w-100"
      value={controllerField.value ? dayjs(controllerField.value) : null}
      placeholder={placeholder || label}
      format="YYYY-MM-DD"
      onChange={(date) => controllerField.onChange(date ? dayjs(date).format("YYYY-MM-DD") : null)}
      allowClear
    />
  ),
});

const ActivityLogs = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

  const normalizedSearch = debouncedValue.trim();

  //currentUser
  const user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = user.roles.some((role) => role.key === "super_admin");

  useEffect(() => {
    setPage(1);
  }, [debouncedValue, filters]);

  const queryParams = useMemo(
    () => ({
      page,
      per_page: PAGE_SIZE,
      ...(normalizedSearch ? { search: normalizedSearch } : {}),
      ...filters,
    }),
    [page, normalizedSearch, filters],
  );

  const { data: res, isLoading, isFetching } = useActivityLogsQuery(queryParams);
  const rows = res?.data?.data ?? [];
  const meta = res?.data?.meta ?? {};
  const loading = isLoading || isFetching;

  const columns = useMemo(
    () => [
      {
        name: t("SERIAL_NUMBER"),
        key: "id",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("id"),
        render: (value) => value ?? EMPTY_VALUE,
      },
      {
        name: t("LOG_NAME"),
        key: "log_name",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("log_name"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: getTranslatedLabel(t, "EVENT", "Event"),
        key: "event",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("event"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: t("Description"),
        key: "description",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("description"),
        render: (_, row) =>
          getLocalizedText(row?.description_ar, row?.description_en) || row?.description || EMPTY_VALUE,
      },
      {
        name: t("CAUSER_TYPE"),
        key: "causer_type",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("causer_type"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: t("USER_NAME"),
        key: "actor",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("actor"),
        render: (_, row) =>
          isSuperAdmin ? (
            <Link to={`/app/user_management/users/${row?.causer?.id}/profile`}>
              {row?.causer?.name || row?.causer?.email || row?.user_id || row?.causer_id || EMPTY_VALUE}
            </Link>
          ) : (
            row?.causer?.name || row?.causer?.email || row?.user_id || row?.causer_id || EMPTY_VALUE
          ),
      },
      {
        name: t("USER_ID"),
        key: "causer",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("causer"),
        render: (_, row) =>
           isSuperAdmin ? (
            <Link to={`/app/user_management/users/${row?.causer?.id}/profile`}>
              {row?.causer?.id || EMPTY_VALUE}
            </Link>
          ) : (
            row?.causer?.id || EMPTY_VALUE
          ),
      },
      {
        name: t("SUBJECT_TYPE"),
        key: "subject_type",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("subject_type"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: t("SUBJECT_ID"),
        key: "subject_id",
        visible: false,
        render: (value) => value ?? EMPTY_VALUE,
      },
      {
        name: t("IP_ADDRESS"),
        key: "ip_address",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("ip_address"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: t("REQUEST_URL"),
        key: "request_url",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("request_url"),
        render: (value) => value || EMPTY_VALUE,
      },
      {
        name: t("USER_AGENT"),
        key: "user_agent",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("user_agent"),
        render: (value) =>
          value ? (
            <span title={value}>
              {String(value).slice(0, 80)}
              {String(value).length > 80 ? "..." : ""}
            </span>
          ) : (
            EMPTY_VALUE
          ),
      },
      {
        name: t("UPDATED_AT"),
        key: "updated_at",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("updated_at"),
        render: (value) => formatDate( value, i18n.language, DATE_TIME_FULL_FORMAT ),
      },
      {
        name: t("CREATED_AT"),
        key: "created_at",
        visible: DEFAULT_VISIBLE_COLUMN_KEYS.includes("created_at"),
        render: (value) =>  formatDate( value, i18n.language, DATE_TIME_FULL_FORMAT ),
      },
    ],
    [getLocalizedText, t],
  );

  const skeletonCount = 10;

  const filterFields = useMemo(
    () => [
      {
        name: "log_name",
        label: getTranslatedLabel(t, "LOG_NAME", "Log name"),
        type: "input",
        colSize: { md: 6 },
      },
      {
        name: "event",
        label: getTranslatedLabel(t, "EVENT", "Event"),
        type: "input",
        colSize: { md: 6 },
      },
      {
        name: "user_id",
        label: getTranslatedLabel(t, "USER_ID", "User ID"),
        type: "input",
        inputType: "number",
        colSize: { md: 6 },
      },
      {
        name: "subject_type",
        label: getTranslatedLabel(t, "SUBJECT_TYPE", "Subject type"),
        type: "input",
        colSize: { md: 6 },
      },
      {
        name: "subject_id",
        label: getTranslatedLabel(t, "SUBJECT_ID", "Subject ID"),
        type: "input",
        inputType: "number",
        colSize: { md: 6 },
      },
      createDateFilterField("created_from", t("FROM_DATE2")),
      createDateFilterField("created_to", t("TO_DATE")),
      {
        name: "ip_address",
        label: getTranslatedLabel(t, "IP_ADDRESS", "IP address"),
        type: "input",
        colSize: { md: 6 },
      },
    ],
    [t],
  );

  return (
    <div>
      <PageHeader title={t("SYSTEM_SECURITY")} sticky={false} />
      <Container fluid>
        <TableC
          data={rows}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={(e) => setSearchValue(e?.target?.value ?? "")}
          searchPlaceholder={getTranslatedLabel(t, "ACTIVITY_LOGS_SEARCH_PLACEHOLDER", "Search activity logs")}
          pagination={{
            current: meta?.current_page ?? page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? PAGE_SIZE,
            onChange: (nextPage) => setPage(nextPage),
          }}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={(nextFilters) => {
            setFilters(nextFilters);
            setPage(1);
          }}
          onFilterReset={() => {
            setFilters({});
            setPage(1);
          }}
          headerKey="id"
          emptyStateTitle={normalizedSearch ? t("NO_RESULTS") : t("NO_DATA")}
          emptyStateDescription={normalizedSearch ? t("NO_RESULTS_DESCRIPTION") : t("NO_DATA_DESCRIPTION")}
          skeletonCount={skeletonCount}
        />
      </Container>
    </div>
  );
};

export default ActivityLogs;
